"use client";

import { useEffect, useState } from "react";

/**
 * The short form of a share URL, fetched ahead of the share gesture.
 *
 * AHEAD OF, not during, and that is the whole reason this is a hook rather
 * than a line inside the share handler. `navigator.share()` has to be called
 * in the same task as the tap that triggered it — awaiting a fetch first
 * spends the user activation and iOS Safari throws `NotAllowedError`. So the
 * short link is resolved when the share drawer opens, and the handler reads a
 * value that is already there.
 *
 * Returns null until it resolves, and null forever if it cannot. Every caller
 * treats that as "share the long URL": a share sheet that does not open
 * because a link could not be shortened would be a straight regression.
 */

/**
 * Module-level and deliberately unbounded-ish: a short link never changes for
 * a given destination, and the alternative is refetching on every drawer open
 * for the same event. Capped so a long session browsing a hundred events
 * cannot grow it without limit.
 */
const CACHE_LIMIT = 200;
const resolved = new Map<string, string | null>();
const inFlight = new Map<string, Promise<string | null>>();

async function fetchShortUrl(longUrl: string): Promise<string | null> {
  const cached = resolved.get(longUrl);
  if (cached !== undefined) return cached;

  const existing = inFlight.get(longUrl);
  if (existing) return existing;

  const request = (async () => {
    try {
      const response = await fetch("/api/short-links", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: longUrl }),
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { shortUrl?: string | null };
      return typeof data.shortUrl === "string" ? data.shortUrl : null;
    } catch {
      return null;
    }
  })();

  inFlight.set(longUrl, request);
  const result = await request;
  inFlight.delete(longUrl);

  // Successes only. Caching a null pins the long URL for the rest of the
  // session after a single blip (dev-server reload, blocked request, slow
  // first compile) — the next drawer open must retry rather than reuse it.
  if (result !== null) {
    if (resolved.size >= CACHE_LIMIT) {
      const oldest = resolved.keys().next();
      if (!oldest.done) resolved.delete(oldest.value);
    }
    resolved.set(longUrl, result);
  }
  return result;
}

export function useShortShareUrl(longUrl: string | null): string | null {
  const [shortUrl, setShortUrl] = useState<string | null>(
    longUrl ? (resolved.get(longUrl) ?? null) : null,
  );

  useEffect(() => {
    if (!longUrl) {
      setShortUrl(null);
      return;
    }

    const cached = resolved.get(longUrl);
    if (cached !== undefined) {
      setShortUrl(cached);
      return;
    }

    // A cancel flag rather than an early return on unmount: under StrictMode's
    // double-invoke the second effect must be the one whose result applies.
    let cancelled = false;
    setShortUrl(null);
    void fetchShortUrl(longUrl).then((result) => {
      if (!cancelled) setShortUrl(result);
    });

    return () => {
      cancelled = true;
    };
  }, [longUrl]);

  return shortUrl;
}
